import { useProgrammaticMove } from '@map-ui/programmatic-move';
import { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import { useLocationGeocode } from '@/shared/lib/location';

// Country-level zoom when centring on the selected country (legacy parity:
// the products/planner map used zoom 5).
const COUNTRY_ZOOM = 5;

// Geocodes a country name (Nominatim via `useLocationGeocode`) and recentres
// the map on it — legacy (V2) parity with `map.tsx`'s `geocodeCountry` +
// `setView`, which centred the map on the selected country. Mount as a child
// of <MapContainer>. A `null`/short name is a no-op. The recentre is flagged
// programmatic so a bounds-driven host doesn't mistake it for a user pan
// (which would write a viewport bbox and re-filter the product list).
export const CountryFocus = ({ name }: { name: string | null | undefined }) => {
  const map = useMap();
  const programmaticRef = useProgrammaticMove();
  const query = (name ?? '').trim();
  const { data } = useLocationGeocode(query);
  // The hook only fetches at length >= 3; first hit is the country itself.
  const top = query.length >= 3 ? (data?.[0] ?? null) : null;
  const key = top ? `${query}:${top.latitude},${top.longitude}` : null;
  useEffect(() => {
    if (!top) return;
    if (programmaticRef) {
      programmaticRef.current = true;
      // Fallback clear if no `moveend` fires (e.g. already centred there).
      // Comfortably longer than Leaflet's default setView animation so the
      // real moveend clears the flag first.
      window.setTimeout(() => {
        programmaticRef.current = false;
      }, 1500);
    }
    // Legacy parity (`map.tsx`): `setView(center, 5, { animate: true })` —
    // no explicit duration, so Leaflet's default (quick) zoom+pan animation.
    map.setView([top.latitude, top.longitude], COUNTRY_ZOOM, {
      animate: true,
    });
  }, [map, key]);
  return null;
};
